import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Event as StreamEvent, StreamChat, Channel } from 'stream-chat';
import {
  NOTIF_CHANNEL,
  DEFAULT_CHAT_NAME,
  DEFAULT_MESSAGE_TEXT,
  StreamEventType,
  NotificationPermission,
  DEFAULT_USER_NAME,
  HIDDEN_CHAT_ROLES,
} from '@features/components/Chat/constants';
import { useChatContext } from 'stream-chat-react';
import { StreamUserRole, TxCenter } from '@features/components/Chat/types';
import { useStreamChatContext } from '@features/contexts/ChatProvider';
import { hasUserAccessToChat } from '@features/components/Chat/utils/userAssignment';
import { AppType, useAppContext } from '@features/contexts/AppProvider';
import { StreamChannelType } from '@/api/apis/chat';
import { checkAndLogNotificationPermission, buildCaseUrl } from '@/utils/helpers';
import { ORGANIZATION_TENANT_PREFIX } from '@/constants/customer.constants';

const NEW_MESSAGE_EVENTS = [StreamEventType.MESSAGE_NEW, StreamEventType.NOTIFICATION_MESSAGE_NEW];

const CHANNEL_ORG_ID_PROP: Partial<Record<string, string>> = {
  [ORGANIZATION_TENANT_PREFIX.TxCenter]: 'txcenter_id',
  [ORGANIZATION_TENANT_PREFIX.OPO]: 'opo_organization_id',
};

const getOrganizationIdFromChannel = (channel: Record<string, unknown>): string | undefined => {
  const prefix = localStorage.getItem('selectedOrganizationPrefix') || '';
  const prop = CHANNEL_ORG_ID_PROP[prefix];
  return prop ? (channel[prop] as string) : undefined;
};

const shouldSkipNotifications = (userId: string, role?: string, channel?: Channel) => {
  if (role && !HIDDEN_CHAT_ROLES.has(role as StreamUserRole)) return false;

  return !hasUserAccessToChat(userId, role, channel);
};

function getChannelByCid(client: StreamChat, cid: string): Channel | undefined {
  if (client.activeChannels && Object.prototype.hasOwnProperty.call(client.activeChannels, cid)) {
    return client.activeChannels[cid];
  }
  return undefined;
}

const useChatNotifications = (txCenters?: TxCenter[] | null) => {
  const { isConnected, chatTypes } = useStreamChatContext();
  const { channel: activeChannel, client: chatClient } = useChatContext();
  const navigate = useNavigate();
  const { type } = useAppContext();
  const isNopConnectPlatform = type === AppType.NopConnectPlatform;

  useEffect(() => {
    if (!chatClient || !isConnected) {
      return () => {};
    }

    const processedMessageIds = new Set<string>();
    const bc = new BroadcastChannel(NOTIF_CHANNEL);

    bc.onmessage = (bcEvent) => {
      const { data } = bcEvent;
      if (data?.type === StreamEventType.PROCESSED && data?.messageId) {
        processedMessageIds.add(data.messageId);
      }
    };

    if (Notification.permission === NotificationPermission.DEFAULT) {
      Notification.requestPermission();
    }

    const handleMessage = (event: StreamEvent) => {
      const userId = chatClient.userID;
      if (!userId) return;

      const channelFromEvent = event.channel as Channel | undefined;
      const channelFromCid = event.cid ? getChannelByCid(chatClient, event.cid) : undefined;

      const eventChannel = channelFromEvent ?? channelFromCid;
      if (shouldSkipNotifications(userId, chatClient.user?.role, eventChannel)) return;

      const channelType = (eventChannel?.type ?? eventChannel?.data?.type) as StreamChannelType;
      if (chatTypes.length && !chatTypes.includes(channelType)) return;

      if (!NEW_MESSAGE_EVENTS.includes(event.type as StreamEventType) || !event.message?.id || !event.cid) return;

      const { message } = event;
      const messageId = message.id;

      const mentionedUserIds = message.mentioned_users?.map((u) => u.id) || [];
      const isUserMentioned = mentionedUserIds.includes(userId);

      const channel = event.channel ?? getChannelByCid(chatClient, event.cid)?.data;
      if (!channel) return;

      const isMuted = chatClient.mutedChannels.some((c) => c?.channel?.cid === event.cid);

      if (isMuted && !isUserMentioned) return;

      if (activeChannel?.cid && event.cid === activeChannel.cid && !document.hidden) return;

      if (processedMessageIds.has(messageId)) return;
      processedMessageIds.add(messageId);

      bc.postMessage({ type: StreamEventType.PROCESSED, messageId });

      if (Notification.permission === NotificationPermission.GRANTED) {
        const txCenterCode = channel.txcenter_id
          ? txCenters?.find((center) => center.id === channel.txcenter_id)?.code
          : null;
        const sender = message.user?.name || DEFAULT_USER_NAME;
        const content = message.text || DEFAULT_MESSAGE_TEXT;
        const chatName = `${channel?.name || DEFAULT_CHAT_NAME}${txCenterCode ? `: ${txCenterCode}` : ''}`;
        const notification = new Notification(chatName, {
          body: `${sender}: ${content}`,
        });

        const channelCaseId = ((channel.caseIds as string[]) || [])[0];

        if (channelCaseId) {
          notification.onclick = () => {
            window.focus();
            navigate(
              buildCaseUrl({
                caseId: channelCaseId,
                ...(!isNopConnectPlatform && {
                  organizationId: getOrganizationIdFromChannel(channel as Record<string, unknown>),
                }),
              }),
            );
          };
        }
      } else {
        // Log when GetStream notification is received but browser notifications are disabled
        const txCenterCode = channel.txcenter_id
          ? txCenters?.find((center) => center.id === channel.txcenter_id)?.code
          : null;
        const sender = message.user?.name || DEFAULT_USER_NAME;
        const chatName = `${channel?.name || DEFAULT_CHAT_NAME}${txCenterCode ? `: ${txCenterCode}` : ''}`;

        checkAndLogNotificationPermission('GetStream', {
          chatName,
          sender,
          txCenterCode,
          channelId: event.cid,
          messageId: message.id,
        });
      }
    };

    chatClient.on(StreamEventType.MESSAGE_NEW, handleMessage);
    chatClient.on(StreamEventType.NOTIFICATION_MESSAGE_NEW, handleMessage);

    return () => {
      chatClient.off(StreamEventType.MESSAGE_NEW, handleMessage);
      chatClient.off(StreamEventType.NOTIFICATION_MESSAGE_NEW, handleMessage);
      bc.close();
    };
  }, [chatClient, navigate, activeChannel?.cid, txCenters, isConnected, chatTypes, isNopConnectPlatform]);
};

export default useChatNotifications;
